import { ShowMoreFooter } from "@/components/ShowMoreFooter";
import { STAGE_ACTIONS_SECTION_IDS } from "@/features/project/projectTopSection/stages/config";
import { FilterListContainer } from "@/features/projects/filter/FilterListContainer";
import { SectionItem } from "@/features/tasks/SectionItem";
import styled from "@emotion/styled";
import { type Opportunity } from "@repo/api";
import { LightMode, ListView, Text, noteIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  FilterCheckbox,
  FilterPill,
  ListViewItemWithActions,
  LocalizedDateAndTime,
  SectionContainer,
} from "@repo/ui";
import { hasItems, isChecked, trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { InlineNoteInput } from "./InlineNoteInput";
import { NoteItem } from "./NoteItem";
import { useNotes } from "./useNotes";

interface NotesSectionProps {
  opportunity: Opportunity;
}

type NotesFilter = "EDITED" | "DELETED";

const DEFAULT_NOTE_COUNT = 3;

const PreWrapText = styled(Text)`
  white-space: pre-wrap;
`;

export const NotesSection = ({ opportunity }: NotesSectionProps) => {
  const { pinnedNotes, unpinnedNotes, deletedNotes } = useNotes(opportunity);
  const [filters, setFilters] = useState<NotesFilter[]>([]);
  const [showAllPinned, setShowAllPinned] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const includeEdited = filters.includes("EDITED");
  const includeDeleted = filters.includes("DELETED");
  const hasActiveNoteFilter = includeEdited;

  const handleCheckFilter = (value: NotesFilter) =>
    filters.includes(value)
      ? setFilters((current) => current.filter((filter) => filter !== value))
      : setFilters((current) => [...current, value]);

  const filteredPinned = hasActiveNoteFilter
    ? pinnedNotes.filter((note) => includeEdited && Boolean(note.updatedAt))
    : pinnedNotes;

  const filteredUnpinned = hasActiveNoteFilter
    ? unpinnedNotes.filter((note) => includeEdited && Boolean(note.updatedAt))
    : unpinnedNotes;

  const showActiveNotes = !includeDeleted || includeEdited;

  const hasNotes = hasItems(filteredPinned) || hasItems(filteredUnpinned);
  const visiblePinned = showAllPinned
    ? filteredPinned
    : filteredPinned.slice(0, DEFAULT_NOTE_COUNT);
  const visibleUnpinned = showAll
    ? filteredUnpinned
    : filteredUnpinned.slice(0, DEFAULT_NOTE_COUNT);
  const hasDeletedNotes = hasItems(deletedNotes);

  const headerAction =
    hasItems(pinnedNotes) || hasItems(unpinnedNotes) || hasDeletedNotes ? (
      <FilterPill
        id="notes-filter"
        label={
          filters.length
            ? `${t("label.filter")} (${filters.length})`
            : `${t("label.filter")}`
        }
        dropDownItems={
          <FilterListContainer id="notes-filter">
            <FilterCheckbox
              id="EDITED"
              value="EDITED"
              label={t("label.edited_at")}
              checked={isChecked(filters, "EDITED")}
              onCheckFilter={handleCheckFilter}
            />
            <FilterCheckbox
              id="DELETED"
              value="DELETED"
              label={t("label.deleted")}
              checked={isChecked(filters, "DELETED")}
              onCheckFilter={handleCheckFilter}
            />
          </FilterListContainer>
        }
        rightAlign
      />
    ) : undefined;

  return (
    <SectionItem
      id={STAGE_ACTIONS_SECTION_IDS.NOTES_SECTION}
      title={t("label.notes")}
      action={headerAction}
    >
      <InlineNoteInput opportunityId={opportunity.id} />

      {showActiveNotes && hasItems(filteredPinned) && (
        <SectionContainer>
          <Text textStyle="Heading.XS">{t("label.pinned_notes")}</Text>
          <ListView id="pinned-notes-list" size="small">
            {visiblePinned.map((note) => (
              <NoteItem key={note.id} note={note} />
            ))}
          </ListView>
          {filteredPinned.length > DEFAULT_NOTE_COUNT && (
            <ShowMoreFooter
              visibleCount={visiblePinned.length}
              totalCount={filteredPinned.length}
              isShowAll={showAllPinned}
              onToggle={() => setShowAllPinned(!showAllPinned)}
            />
          )}
        </SectionContainer>
      )}

      {(showActiveNotes && hasItems(filteredUnpinned)) ||
      (includeDeleted && hasDeletedNotes) ? (
        <SectionContainer>
          <Text textStyle="Heading.XS">{t("label.all_notes")}</Text>
          <ListView id="notes-list" size="small">
            {includeDeleted &&
              deletedNotes.map((deletedNote) => (
                <ListViewItemWithActions
                  key={deletedNote.id}
                  inset
                  icon={noteIcon}
                  title={
                    <PreWrapText textStyle="Body.M">
                      {deletedNote.note}
                    </PreWrapText>
                  }
                  addOn={
                    <Text
                      tagName="span"
                      color={LightMode.colourTextAndIcon3}
                      textStyle="Body.S"
                    >
                      {t("label.deleted_by")}{" "}
                      <CoWorkerName
                        email={deletedNote.deletedBy}
                        tagName="span"
                        color={LightMode.colourTextAndIcon3}
                        textStyle="Body.S"
                      />
                      {" · "}
                      <LocalizedDateAndTime date={deletedNote.deletedAt} />
                    </Text>
                  }
                />
              ))}
            {showActiveNotes &&
              visibleUnpinned.map((note) => (
                <NoteItem key={note.id} note={note} />
              ))}
          </ListView>
          {showActiveNotes && filteredUnpinned.length > DEFAULT_NOTE_COUNT && (
            <ShowMoreFooter
              visibleCount={visibleUnpinned.length}
              totalCount={filteredUnpinned.length}
              isShowAll={showAll}
              onToggle={() => {
                setShowAll(!showAll);
                trackAnalytics("view_all_notes");
              }}
            />
          )}
        </SectionContainer>
      ) : null}

      {includeDeleted && !hasDeletedNotes && !showActiveNotes && (
        <SectionContainer>
          <Text>{t("prompt.no_deleted_notes")}</Text>
        </SectionContainer>
      )}

      {showActiveNotes && !hasNotes && (
        <SectionContainer>
          <Text>{t("prompt.no_notes")}</Text>
        </SectionContainer>
      )}
    </SectionItem>
  );
};
